export const PROFILE_PICTURE_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
export const PROFILE_PICTURE_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const PROFILE_PICTURE_OUTPUT_SIZE = 512;
export const PROFILE_PICTURE_PREVIEW_SIZE = 260;
export const PROFILE_PICTURE_ROUND_PREVIEW_SIZE = 112;
export const PROFILE_PICTURE_MIN_ZOOM = 1;
export const PROFILE_PICTURE_MAX_ZOOM = 3;

export interface ProfilePictureCropState {
  x: number;
  y: number;
  zoom: number;
}

interface ProfilePicturePreviewMetrics {
  width: number;
  height: number;
  left: number;
  top: number;
}

const clampPercentage = (value: number) => Math.min(100, Math.max(0, Math.round(value)));
const clampZoom = (value: number) => Math.min(PROFILE_PICTURE_MAX_ZOOM, Math.max(PROFILE_PICTURE_MIN_ZOOM, value));

const replaceFileExtension = (fileName: string, nextExtension: string) => {
  const normalizedFileName = fileName.trim() || `upload.${nextExtension}`;
  const fileNameWithoutExtension = normalizedFileName.replace(/\.[^.]+$/, '');
  return `${fileNameWithoutExtension}.${nextExtension}`;
};

const canvasToBlob = (canvas: HTMLCanvasElement, mimeType: string, quality?: number) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Unable to prepare the selected image for upload.'));
      return;
    }

    resolve(blob);
  }, mimeType, quality);
});

export const readImageFileAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();

  reader.onload = () => {
    if (typeof reader.result === 'string') {
      resolve(reader.result);
      return;
    }

    reject(new Error('Unable to prepare the selected image.'));
  };

  reader.onerror = () => reject(new Error('Unable to prepare the selected image.'));
  reader.readAsDataURL(file);
});

export const loadImageSource = (source: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();

  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Unable to read the selected image file.'));
  image.src = source;
});

export const getProfilePicturePreviewMetrics = (
  imageWidth: number,
  imageHeight: number,
  frameSize: number,
  cropState: ProfilePictureCropState,
): ProfilePicturePreviewMetrics => {
  const normalizedWidth = Math.max(1, imageWidth);
  const normalizedHeight = Math.max(1, imageHeight);
  const baseScale = Math.max(frameSize / normalizedWidth, frameSize / normalizedHeight);
  const scale = baseScale * clampZoom(cropState.zoom);
  const width = normalizedWidth * scale;
  const height = normalizedHeight * scale;
  const horizontalOverflow = Math.max(width - frameSize, 0);
  const verticalOverflow = Math.max(height - frameSize, 0);

  return {
    width,
    height,
    left: -horizontalOverflow * (clampPercentage(cropState.x) / 100),
    top: -verticalOverflow * (clampPercentage(cropState.y) / 100),
  };
};

export const createCroppedProfilePictureFile = async (
  imageSrc: string,
  originalFile: File,
  cropState: ProfilePictureCropState,
): Promise<File> => {
  const image = await loadImageSource(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = PROFILE_PICTURE_OUTPUT_SIZE;
  canvas.height = PROFILE_PICTURE_OUTPUT_SIZE;

  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Your browser could not prepare this profile picture.');
  }

  const previewMetrics = getProfilePicturePreviewMetrics(
    image.naturalWidth || image.width,
    image.naturalHeight || image.height,
    PROFILE_PICTURE_OUTPUT_SIZE,
    cropState,
  );

  const preferredMimeTypes = originalFile.type === 'image/jpeg'
    ? ['image/jpeg']
    : ['image/webp', 'image/jpeg'];
  const qualities = [0.92, 0.84, 0.76, 0.68];
  let smallestCandidate: {blob: Blob; fileName: string} | null = null;

  for (const mimeType of preferredMimeTypes) {
    const extension = mimeType === 'image/webp' ? 'webp' : 'jpg';

    for (const quality of qualities) {
      context.clearRect(0, 0, canvas.width, canvas.height);

      if (mimeType === 'image/jpeg') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
      }

      context.drawImage(image, previewMetrics.left, previewMetrics.top, previewMetrics.width, previewMetrics.height);

      const blob = await canvasToBlob(canvas, mimeType, quality);
      const nextFileName = replaceFileExtension(originalFile.name, extension);

      if (!smallestCandidate || blob.size < smallestCandidate.blob.size) {
        smallestCandidate = {blob, fileName: nextFileName};
      }

      if (blob.size <= PROFILE_PICTURE_MAX_FILE_SIZE_BYTES) {
        return new File([blob], nextFileName, {
          type: blob.type || mimeType,
          lastModified: Date.now(),
        });
      }
    }
  }

  if (!smallestCandidate) {
    throw new Error('Unable to optimize this profile picture for upload.');
  }

  return new File([smallestCandidate.blob], smallestCandidate.fileName, {
    type: smallestCandidate.blob.type || 'image/jpeg',
    lastModified: Date.now(),
  });
};
